import { useEffect, useRef } from 'react';

/**
 * Close-on-Escape for a modal, where only the TOPMOST modal reacts. Nested
 * modals each register here; a single Escape closes just the innermost one
 * (not the whole stack). Also used so a scrim/keydown on a child doesn't reach
 * its parent. Pair with `stopPropagation()` on the scrim's onClick for clicks.
 */
const stack: Array<() => void> = [];

export function useModalEscape(onClose: () => void): void {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    const token = () => ref.current();
    stack.push(token);
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && stack[stack.length - 1] === token) {
        e.stopPropagation();
        ref.current();
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      const i = stack.indexOf(token);
      if (i >= 0) stack.splice(i, 1);
    };
  }, []);
}
